import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, HelpCircle, Users } from 'lucide-react';
import Avatar from './Avatar';
import { Ring, MiniDots, DotStrip, Faces } from './charts';
import { BuffyRead } from './HomeworkReport';
import QuestionTeachingNote from './QuestionTeachingNote';
import StudentOverviewModal from './StudentOverviewModal';
import ChatActionChip from './ChatActionChip';
import BuffyMarkdown from './BuffyMarkdown';
import { StudentQuestion } from './StudentQuestion';
import { InlineDialog } from './ui';
import { SlotBlock, SlotContext, InlinePanel } from './inlineSlot';
import { teacherApi, fetchRoster } from '../data/api';
import { fetchHomeworkReport, classAverage, pct } from '../data/homeworkReport';
import { DIALOG_ACTION_KINDS } from '../data/actionRegistry';
import { DraftShell, bandWord } from './PracticeSetBox';
import { topicById, topicsFromLegacy } from '../data/homeworkV4';
import { Shrinkable, ShrinkButton } from './Shrinkable';
import { dateLabel } from '../data/teacherHomeModel';
import { holdOpenLink } from '../data/buffyMath';
import './buffyInline.css';

/* Interactive text in Buffy's replies (2026-10-14, Sandy: "more interactive
   text… like ChatGPT… small images in the text", approved from the
   /teacher-dashboard/buffy-inline preview). What Buffy mentions becomes a
   small live chip inside the sentence: hover for a peek, click and it opens
   right in the conversation (never a pop-up):
   - questions (showQuestion tags): the real question card + Buffy's teaching idea
   - actions (the dialog tags): the dashboard's own dialog, drawn in place
   - [Name](student:ID): the student, with their overview
   - [Title](hw:ID): the practice set, whose peek is its review
   - [6 students](students:ID,ID,…): a group, one chip that opens the list
   Every number in a chip comes from the class's own data; Buffy only names
   the record. An id the app can't find shows as plain text. */

const ReplyContext = createContext(null);
const ID = /^[\w.-]{1,64}$/;
const first = name => String(name || '').split(' ')[0];
const pretty = c => String(c || '').replace(/-/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());
const cleanLabel = t => String(t || '').replace(/[[\]]/g, '').replace(/\s+/g, ' ').trim().slice(0, 80);
// A mention opens only what its words name (2026-10-14: Buffy wrote "Sim
// Student 32" with Sim Student 2's id). A first name alone counts; a set's
// title may be shortened from either end.
const norm = t => String(t || '').toLowerCase().replace(/[\u2010-\u2015]/g, '-').replace(/[^a-z0-9 -]/g, ' ').replace(/\s+/g, ' ').trim();
export const namesStudent = (label, name) => { const l = norm(label); const n = norm(name); return !!l && (l === n || n.split(' ')[0] === l); };
export const namesSet = (label, title) => { const l = norm(label); const t = norm(title); return !!l && !!t && (t === l || t.startsWith(l) || l.startsWith(t)); };
const textOf = children => (Array.isArray(children) ? children.map(textOf).join('') : typeof children === 'string' || typeof children === 'number' ? String(children) : children?.props ? textOf(children.props.children) : '');

// ---- data (cached per page load) -----------------------------------------------

const questions = new Map();
function loadQuestion(auth, classId, item) {
  const key = `${classId}|${item.record}|${item.student || ''}`;
  if (!questions.has(key)) {
    questions.set(key, teacherApi(auth, 'getQuestionDetail', { classID: classId, recordID: item.record, userId: item.student })
      .then(q => {
        if (!q?.questionText) throw new Error('no question');
        return item.name && q.student ? { ...q, student: { ...q.student, name: item.name } } : q;
      })
      .catch(err => { questions.delete(key); throw err; }));
  }
  return questions.get(key);
}
const reports = new Map();
function loadReport(auth, classId, hwId) {
  const key = `${classId}|${hwId}`;
  if (!reports.has(key)) reports.set(key, fetchHomeworkReport(auth, classId, hwId).catch(err => { reports.delete(key); throw err; }));
  return reports.get(key);
}
function useLoad(load, deps, eager = false) {
  const [state, setState] = useState({ status: 'idle' });
  const started = useRef(false);
  const start = () => {
    if (started.current) return;
    started.current = true;
    setState({ status: 'loading' });
    load().then(data => setState({ status: 'ready', data })).catch(() => { started.current = false; setState({ status: 'error' }); });
  };
  useEffect(() => { if (eager) start(); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return [state, start];
}

// ---- the chip and its peek ----------------------------------------------------

const canHover = () => typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches;
const calm = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Where the peek starts (2026-10-14, Sandy: "growing out of the thing"): a
// sliver as wide as the chip, at the edge that touches it, nudged onto the
// chip. It unrolls to the full card from there and rolls back up on close.
function seed(anchor, pos, el) {
  const r = anchor.getBoundingClientRect();
  const w = pos.width;
  const h = el.offsetHeight;
  const below = pos.top >= r.bottom;
  const l = Math.max(0, Math.min(r.left - pos.left, w - 24));
  const rt = Math.max(0, Math.min(pos.left + w - r.right, w - 24 - l));
  const strip = Math.max(0, h - 6);
  return { clip: `inset(${below ? 0 : strip}px ${rt}px ${below ? strip : 0}px ${l}px round 6px)`, dy: below ? -8 : 8 };
}
const OPEN_CLIP = 'inset(0px 0px 0px 0px round 12px)';

// Under the chip, above it near the bottom of the window, always 16px inside.
// An interactive peek takes clicks; it's portaled, so its clicks stop here
// instead of reaching the chip.
function Peek({ anchor, width, interactive, leaving, onGone, onEnter, onLeave, children }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const grown = useRef(false);
  // Placed again when its content grows (loading, then the review), and only
  // set when the position actually changes.
  useLayoutEffect(() => {
    const place = () => {
      const r = anchor.getBoundingClientRect();
      const w = Math.min(width, window.innerWidth - 32);
      const h = ref.current ? ref.current.offsetHeight : 160;
      const left = Math.round(Math.max(16, Math.min(r.left, window.innerWidth - 16 - w)));
      const top = Math.round(r.bottom + 8 + h > window.innerHeight - 16 ? Math.max(16, r.top - 8 - h) : r.bottom + 8);
      setPos(p => (p && p.left === left && p.top === top && p.width === w ? p : { left, top, width: w }));
    };
    place();
    const ro = typeof ResizeObserver !== 'undefined' && ref.current ? new ResizeObserver(place) : null;
    ro?.observe(ref.current);
    return () => ro?.disconnect();
  }, [anchor, width]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!pos || !el || grown.current) return;
    grown.current = true;
    if (calm() || !el.animate) return;
    const from = seed(anchor, pos, el);
    el.animate([
      { opacity: 0, clipPath: from.clip, transform: `translateY(${from.dy}px)` },
      { opacity: 1, offset: 0.35 },
      { opacity: 1, clipPath: OPEN_CLIP, transform: 'none' },
    ], { duration: 240, easing: 'cubic-bezier(.2,.8,.2,1)' });
  }, [pos, anchor]);
  useEffect(() => {
    const el = ref.current;
    if (!leaving) return undefined;
    if (!el || !pos || calm() || !el.animate) { onGone(); return undefined; }
    const from = seed(anchor, pos, el);
    const a = el.animate([
      { opacity: 1, clipPath: OPEN_CLIP, transform: 'none' },
      { opacity: 0, clipPath: from.clip, transform: `translateY(${from.dy / 2}px)` },
    ], { duration: 140, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' });
    a.onfinish = onGone;
    return () => { a.onfinish = null; a.cancel(); };
  }, [leaving]); // eslint-disable-line react-hooks/exhaustive-deps
  return createPortal(<div ref={ref} className={`bi-peek${interactive ? ' is-interactive' : ''}`} role={interactive ? 'dialog' : 'tooltip'}
    onMouseEnter={onEnter} onMouseLeave={onLeave} onClick={e => e.stopPropagation()} style={pos || { visibility: 'hidden', left: 0, top: 0, width }}>{children}</div>, document.body);
}

// `peek`: its content, or a function of close() when it has buttons in it.
export function Chip({ tone, icon, children, onClick, open, done, peek, onPeek, interactive = false, peekWidth = 300, label }) {
  const ref = useRef(null);
  const showT = useRef(0);
  const hideT = useRef(0);
  const [hovering, setHovering] = useState(false);
  const [leaving, setLeaving] = useState(false);
  useEffect(() => () => { clearTimeout(showT.current); clearTimeout(hideT.current); }, []);
  // A short wait so passing over a chip doesn't flash its peek; coming back
  // while it rolls up opens it again where it is.
  const show = () => {
    if (!peek || !canHover()) return;
    onPeek?.(); clearTimeout(hideT.current); clearTimeout(showT.current);
    if (leaving) { setLeaving(false); setHovering(true); return; }
    showT.current = setTimeout(() => setHovering(true), 80);
  };
  const hide = () => { if (hovering) setLeaving(true); setHovering(false); };
  const leave = () => { clearTimeout(showT.current); clearTimeout(hideT.current); hideT.current = setTimeout(hide, interactive ? 220 : 0); };
  const close = () => { clearTimeout(showT.current); clearTimeout(hideT.current); setHovering(false); setLeaving(false); };
  const Tag = onClick ? 'button' : 'span';
  return <Tag ref={ref} type={onClick ? 'button' : undefined} tabIndex={!onClick && peek ? 0 : undefined} aria-label={label} aria-expanded={onClick && open !== undefined ? !!open : undefined}
    className={`bi-chip bi-${tone}${open ? ' is-open' : ''}${done ? ' is-done' : ''}${onClick ? ' is-live' : ''}`}
    onClick={onClick ? e => { close(); onClick(e); } : undefined} onMouseEnter={show} onMouseLeave={leave}
    onFocus={interactive ? undefined : show} onBlur={interactive ? undefined : close}>
    {(icon || done) && <span className="bi-ico" aria-hidden="true">{done ? <Check /> : icon}</span>}
    <span className="bi-label">{children}</span>
    {(hovering || leaving) && ref.current && <Peek anchor={ref.current} width={peekWidth} interactive={interactive} leaving={leaving} onGone={() => setLeaving(false)} onEnter={() => clearTimeout(hideT.current)} onLeave={leave}>
      {typeof peek === 'function' ? peek(close) : peek}</Peek>}
  </Tag>;
}

// One open panel per chat (2026-10-14, Sandy: opening another should close
// the one already expanded). Questions, students, groups and reviews take
// part; an action's dialog doesn't, since it may hold a code being typed.
const panels = typeof EventTarget !== 'undefined' ? new EventTarget() : null;
function useOnePanel(open, close) {
  const me = useRef({});
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open || !panels) return undefined;
    panels.dispatchEvent(new CustomEvent('open', { detail: me.current }));
    const onOpen = e => { if (e.detail !== me.current) closeRef.current(); };
    panels.addEventListener('open', onOpen);
    return () => panels.removeEventListener('open', onOpen);
  }, [open]);
}

const Loading = ({ children = 'Loading…' }) => <div className="buffy-chat-thinking bi-loading" role="status"><span className="buffy-chat-dots" aria-hidden="true"><i /><i /><i /></span><span>{children}</span></div>;

// ---- questions --------------------------------------------------------------------

// The topic and how hard, in the teacher's words (2026-10-16: "Close · Level 5"
// meant nothing to a teacher).
// A question's topic as the teacher knows it: the catalog topic its concept
// maps to, else its own name without the bank's "SEC" prefix.
const topicName = q => {
  const c = String(q.concept || '');
  for (const f of ['reading', 'math']) {
    const id = topicsFromLegacy(f, [c])[0];
    const t = id && topicById(f, id);
    if (t) return t.label;
  }
  return pretty(q.skill?.label || c).replace(/^Sec\s+/i, '');
};
const qTitle = q => `${topicName(q)}${Number.isInteger(q.band) ? ` · ${bandWord(q.band)} question` : ''}`;
const qRight = q => (q.pCorrect != null ? `${Math.round(q.pCorrect * 100)}% of all students get it right` : null);

// The pop-out's question view, in the conversation: the question as students
// see it (their pick, the answer, what each wrong pick means) and Buffy's
// teaching idea with its own follow-up box.
const NOTES = { current: new Map() };
// lead(q): a sentence that ties the question to what the teacher opened it
// from (the set review), in place of the plain title line.
// title (optional): what the folded tile says, from where it was opened.
export function QuestionCard({ q, record, classId, lead, title }) {
  const said = lead?.(q);
  return <Shrinkable title={title || qTitle(q)} label={q.student?.name ? `${q.student.name}’s answer` : 'Question'}><div className="bi-qcard">
    <ShrinkButton className="bi-qcard-shrink" />
    {said ? <p className="bi-qcard-lead">{said}{qRight(q) && <span>{qRight(q)}.</span>}</p>
      : <p className="bi-qcard-head"><b>{qTitle(q)}</b>{qRight(q) && <span>{qRight(q)}</span>}</p>}
    <div className="buffy-question-paper"><StudentQuestion q={q} /></div>
    <div className="bi-qcard-note"><QuestionTeachingNote q={q} record={record} classId={classId} cache={NOTES} /></div>
  </div></Shrinkable>;
}

function QuestionChip({ item }) {
  const { auth, classId, streaming } = useContext(ReplyContext);
  const [open, setOpen] = useState(false);
  // Loaded as soon as the reply finishes, so its peek is ready on hover.
  const [state, load] = useLoad(() => loadQuestion(auth, classId, item), [item.record, item.student, !!streaming], !streaming);
  useOnePanel(open, () => setOpen(false));
  const q = state.data;
  return <>
    <Chip tone="question" icon={<HelpCircle />} label={item.label} open={open} onPeek={load} peekWidth={340}
      onClick={() => { load(); setOpen(o => !o); }}
      peek={q ? <><p className="bi-peek-title">{qTitle(q)}</p><div className="bi-qthumb"><div><StudentQuestion q={q} preview /></div></div>{qRight(q) && <p className="bi-peek-foot">{qRight(q)}</p>}</>
        : state.status === 'error' ? <p className="bi-peek-line">This question didn’t load.</p> : <Loading>Opening the question…</Loading>}>
      {item.label}
    </Chip>
    <InlinePanel open={open}>
      {q ? <QuestionCard q={q} record={item.record} classId={classId} title={item.label} /> : state.status === 'error'
        ? <p className="bi-note">Couldn’t load this question just now. <button type="button" className="bi-link" onClick={load}>Try again</button></p> : <Loading>Opening the question…</Loading>}
    </InlinePanel>
  </>;
}

// ---- students ---------------------------------------------------------------------

function StudentChip({ id, label }) {
  const { auth, classId, students } = useContext(ReplyContext);
  const known = (students || []).find(s => s.userId === id);
  const [state] = useLoad(() => fetchRoster(auth, classId), [classId], !known);
  const pool = [...(students || []), ...(state.data || [])];
  // The student the words name: the id's student when the name agrees,
  // otherwise whoever has exactly that name, otherwise plain text.
  const byId = pool.find(r => r.userId === id);
  const s = byId && (!label || namesStudent(label, byId.name)) ? byId : pool.find(r => norm(r.name) === norm(label)) || null;
  const [open, setOpen] = useState(false);
  useOnePanel(open, () => setOpen(false));
  if (!s) return <>{label}</>;
  const name = s.name || label;
  const sid = s.userId;
  return <>
    <Chip tone="student" label={name} open={open} onClick={() => setOpen(o => !o)}
      peek={<div className="bi-peek-head"><Avatar name={name} size={30} /><span><b>{name}</b><small>{s.email || 'Click for their overview'}</small></span></div>}>
      {label || first(name)}
    </Chip>
    <InlinePanel open={open}>
      <InlineDialog.Provider value>{open && <StudentOverviewModal classID={classId} auth={auth} student={{ userId: sid, name }} onClose={() => setOpen(false)} />}</InlineDialog.Provider>
    </InlinePanel>
  </>;
}

// Three or more students named together (2026-10-14): one chip, their faces
// as its icon. The peek lists them; a click opens the list in the chat, and
// each name there opens that student's overview under it.
function GroupChip({ ids, label }) {
  const { auth, classId, students } = useContext(ReplyContext);
  const known = ids.every(id => (students || []).some(s => s.userId === id));
  const [state] = useLoad(() => fetchRoster(auth, classId), [classId], !known);
  const pool = [...(students || []), ...(state.data || [])];
  const people = ids.map(id => pool.find(s => s.userId === id)).filter(Boolean);
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(null);
  useOnePanel(open, () => setOpen(false));
  if (!people.length) return <>{label}</>;
  const names = people.map(s => s.name);
  return <>
    <Chip tone="group" icon={<Faces names={names.slice(0, 3)} max={3} size={16} as="span" />} label={`${label}: ${names.join(', ')}`} open={open} onClick={() => setOpen(o => !o)} peekWidth={260}
      peek={<><p className="bi-peek-title">{label}</p><ul className="bi-peek-people">{people.slice(0, 8).map(s => <li key={s.userId}><Avatar name={s.name} size={20} as="span" /><span>{s.name}</span></li>)}</ul>
        {people.length > 8 && <p className="bi-peek-foot">+{people.length - 8} more</p>}</>}>
      {label}
    </Chip>
    <InlinePanel open={open}>
      <section className="bi-panel bc-card">
        <header className="sc-strip"><Users aria-hidden="true" /><span className="bi-panel-title">{label}</span></header>
        <ul className="bi-people">{people.map(s => <li key={s.userId}>
          <button type="button" className="bi-person" aria-expanded={shown === s.userId} onClick={() => setShown(v => (v === s.userId ? null : s.userId))}>
            <Avatar name={s.name} size={26} as="span" /><span className="bi-person-name">{s.name}</span><ChevronDown className="bi-person-chev" aria-hidden="true" /></button>
          {shown === s.userId && <div className="bi-person-more"><InlineDialog.Provider value><StudentOverviewModal classID={classId} auth={auth} student={{ userId: s.userId, name: s.name }} onClose={() => setShown(null)} /></InlineDialog.Provider></div>}
        </li>)}</ul>
      </section>
    </InlinePanel>
  </>;
}

// ---- practice sets: the peek is the review ----------------------------------------

const MiniBar = ({ value, low }) => <span className="bi-minibar" aria-hidden="true"><i style={{ width: `${Math.max(3, Math.min(100, value || 0))}%` }} className={low ? 'is-low' : ''} /></span>;
const weakest = topics => [...(topics || [])].filter(t => t.accuracy != null).sort((a, b) => a.accuracy - b.accuracy);
const nameOf = (report, uid) => report.students?.find(s => s.userId === uid)?.name || 'A student';
const topMistake = report => (report.mistakes || []).find(m => m.kind === 'trap') || (report.mistakes || [])[0] || null;

function Review({ report, pick, full = false }) {
  const avg = classAverage(report.students || []);
  const topics = weakest(report.topics).slice(0, 3);
  const miss = topMistake(report);
  const lowId = topics[0]?.id;
  return <div className={`bi-rv${full ? ' is-full' : ''}`}>
    {avg != null && <div className="bi-rv-top">
      <span className="bi-rv-stat"><b>{pct(avg)}</b><small>average</small></span>
      {full ? <DotStrip points={(report.students || []).map(s => ({ id: s.userId, label: s.name, value: s.accuracy }))} average={avg} named={1} />
        : <MiniDots values={(report.students || []).map(s => s.accuracy)} width={196} height={36} />}
    </div>}
    {topics.length > 0 && <div className="bi-rv-topics">{topics.map(t => <div key={t.id} className="bi-rv-row">
      <span className="bi-rv-name">{t.label}</span><MiniBar value={t.accuracy} low={t.id === lowId} /><b>{pct(t.accuracy)}</b></div>)}</div>}
    {miss && <button type="button" className="bi-rv-row bi-rv-miss" disabled={!miss.examples?.length} onClick={() => pick?.('mistake')}>
      <b className="bi-rv-count">{miss.count}</b><span className="bi-rv-name">{miss.label}</span>
      <Faces names={(miss.students || []).map(uid => nameOf(report, uid))} max={3} size={18} as="span" />
      {miss.examples?.length > 0 && <ArrowRight className="bi-rv-go" aria-hidden="true" />}</button>}
  </div>;
}

// The review opened from a set's name in Buffy's reply (2026-10-16, Sandy:
// the boxes and the type should match the practice-set box; "1 Supplements"
// on its own line was unclear). Same lilac tile, strip and title as the edit
// box; one row per topic (weakest first) with its accuracy, and a topic's
// most common mistake as a line under that topic: what students did (when
// the mistake has a description), how many wrong answers, who, and See it.
const lowerFirst = t => (t ? `${t[0].toLowerCase()}${t.slice(1)}` : t);
// "Sim Student 1 missed 1 question here." / "4 students picked a cause the
// text never states." (a mistake with a description says what they did).
export function missSentence(m, names) {
  const n = names.length;
  const who = n === 1 ? names[0] : n === 2 ? `${names[0]} and ${names[1]}` : n ? `${n} students` : 'Students';
  if (m.kind === 'trap' && m.label) return `${who} ${lowerFirst(m.label).replace(/\.$/, '')}.`;
  const q = m.count === 1 ? '1 question' : `${m.count || 'some'} questions`;
  return `${who} missed ${q} here.`;
}
// The sentence above the question a mistake opens.
const missLead = (topic, names) => q => {
  const who = q.student?.name || (names.length === 1 ? names[0] : 'A student');
  const picks = q.student?.picked && q.correct ? ` They picked ${q.student.picked}; the right answer is ${q.correct}.` : q.correct ? ` The right answer is ${q.correct}.` : '';
  return `Here’s the ${topic} question ${who} missed.${picks} `;
};
function FullReview({ report, title, done, total, onMistake, shownKey, id }) {
  const avg = classAverage(report.students || []);
  const topics = weakest(report.topics);
  const lowId = topics[0]?.id;
  const mistakes = report.mistakes || [];
  const used = new Set();
  const forTopic = t => {
    const m = mistakes.find(x => !used.has(x) && (x.topicId === t.id || (!x.topicId && x.label === t.label)));
    if (m) used.add(m);
    return m;
  };
  const rows = topics.map(t => ({ t, m: forTopic(t) }));
  const loose = mistakes.filter(m => !used.has(m)).slice(0, 1);
  const missLine = (m, key, topic) => {
    const names = (m.students || []).map(uid => nameOf(report, uid));
    const see = names.length > 1 ? 'See one they missed' : 'See what they missed';
    return <button type="button" className="bi-fr-miss" aria-expanded={shownKey === key} disabled={!m.examples?.length}
      onClick={() => onMistake(shownKey === key ? null : key, { ...m, lead: missLead(topic || 'practice', names), title: `${topic || 'Practice'} question` })}>
      <span>{missSentence(m, names)}</span>
      {m.examples?.length > 0 && <span className="bi-fr-see">{shownKey === key ? 'Hide it' : see}<ChevronDown aria-hidden="true" /></span>}
    </button>;
  };
  return <DraftShell label="How it’s going" strip={false} className="psb bi-fr" shrinkTitle={title} shrinkLabel={`${done} of ${total} finished`}>
    <div className="dc-body">
      <h3 className="bi-fr-title">{title}</h3>
      <div className="bi-fr-read"><BuffyRead report={report} compact /></div>
      {avg != null && <div className="bi-fr-avg"><span className="bi-fr-stat"><b>{pct(avg)}</b><small>average</small></span>
        <DotStrip points={(report.students || []).map(s => ({ id: s.userId, label: s.name, value: s.accuracy }))} average={avg} named={1} /></div>}
      {rows.length > 0 && <ul className="psb-list bi-fr-list">{rows.map(({ t, m }) => <li key={t.id} className="bi-fr-row">
        <div className="bi-fr-line"><span className="bi-fr-name">{t.label}</span><MiniBar value={t.accuracy} low={t.id === lowId} /><b>{pct(t.accuracy)}</b></div>
        {m && missLine(m, `t:${t.id}`, t.label)}
      </li>)}{loose.map((m, i) => <li key={`m${i}`} className="bi-fr-row">{missLine(m, `m:${i}`)}</li>)}</ul>}
    </div>
    <div className="bc-foot"><p>Each student got different questions, so this is by topic.</p>
      <div className="bc-actions"><Link className="bc-link" to={`/teacher-dashboard/assignments?report=${encodeURIComponent(id)}`}>Open in Practice<ArrowRight size={14} aria-hidden="true" /></Link></div></div>
  </DraftShell>;
}

function HomeworkChip({ id, label }) {
  const { auth, classId } = useContext(ReplyContext);
  const [state] = useLoad(() => loadReport(auth, classId, id), [classId, id], true);
  const [open, setOpen] = useState(null); // null | 'review' | 'mistake'
  const [picked, setPicked] = useState(null); // { key, m }: a mistake opened from the review
  useOnePanel(open, () => setOpen(null));
  const r = state.data;
  const c = r?.completion || {};
  const done = c.done ?? 0;
  const total = c.assigned ?? 0;
  const working = Math.max(0, (c.started ?? 0) - done);
  const title = r?.set?.title || label;
  const due = r?.set?.dueDate ? `Due ${dateLabel(r.set.dueDate)}` : null;
  const sub = [due, r ? `${done} of ${total} finished` : null].filter(Boolean).join(' · ');
  const miss = r && topMistake(r);
  const show = key => setOpen(o => (o === key ? null : key));
  const example = miss?.examples?.[0];
  // A set whose title doesn't match the words is a wrong id: plain text.
  if (r && label && r.set?.title && !namesSet(label, r.set.title)) return <>{label}</>;
  return <>
    <Chip tone="hw" icon={<Ring done={done} working={working} total={total} size={16} stroke={3} />} label={`${title}${r ? `, ${done} of ${total} finished` : ''}`}
      open={open === 'review'} onClick={() => show('review')} interactive={!!r} peekWidth={340}
      peek={close => (r ? <>
        <div className="bi-peek-head"><Ring done={done} working={working} total={total} size={30} stroke={4} /><span><b>{title}</b><small>{sub}</small></span></div>
        <Review report={r} pick={key => { close(); setOpen(key); }} />
      </> : state.status === 'error' ? <p className="bi-peek-line">The review isn’t available for this set.</p> : <Loading>Reading their answers…</Loading>)}>
      {label || title}
    </Chip>
    <InlinePanel open={open === 'review'}>
      {r ? <FullReview report={r} title={title} done={done} total={total} id={id} shownKey={picked?.key}
        onMistake={(key, m) => setPicked(key ? { key, m } : null)} />
        : state.status === 'error' ? <p className="bi-note">The review isn’t available for this set.</p> : <Loading>Reading their answers…</Loading>}
    </InlinePanel>
    {open === 'review' && picked?.m?.examples?.[0] && <MistakeCard key={picked.key} open example={picked.m.examples[0]} label={picked.m.label} lead={picked.m.lead} title={picked.m.title} />}
    {example && <MistakeCard open={open === 'mistake'} example={example} label={miss.label} />}
  </>;
}

function MistakeCard({ open, example, lead, title }) {
  const { auth, classId } = useContext(ReplyContext);
  const item = { record: example.recordID, student: example.userId };
  const [state, load] = useLoad(() => loadQuestion(auth, classId, item), [example.recordID, example.userId]);
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return <InlinePanel open={open}>
    {state.data ? <QuestionCard q={state.data} record={item.record} classId={classId} lead={lead} title={title} /> : state.status === 'error' ? <p className="bi-note">Couldn’t load this question just now.</p> : <Loading>Opening the question…</Loading>}
  </InlinePanel>;
}

// ---- the reply ----------------------------------------------------------------------

// What a reply can show inline: its question and action tags (with the slot
// where Buffy wrote each), plus mentions written as markdown links.
// items: [{ id, slot?, kind, label, params?, action? }]
export function replyItems({ questions = [], dialogs = [], handoffs = [] }) {
  const list = [
    ...questions.map(q => ({ slot: q.slot, kind: 'showQuestion', label: cleanLabel(q.label || (q.name ? `${q.name}’s answer` : 'The question')), params: q })),
    ...dialogs.map(a => ({ slot: a.slot, kind: a.kind, label: cleanLabel(a.text), action: a })),
    ...handoffs.filter(a => a.params?.type === 'unit').map(a => ({ slot: a.slot, kind: 'unit', label: cleanLabel(a.text), action: a })),
  ];
  return list.map((it, i) => ({ ...it, id: it.slot != null ? `s${it.slot}` : `t${i}` }));
}

const enc = p => (p ? `~${encodeURIComponent(p).replace(/\(/g, '%28').replace(/\)/g, '%29')}` : '');
// The reply's markdown with every chip as a #bb- link, and the punctuation right
// after a chip carried into its link so the two never split across lines.
// Buffy sometimes puts each item on its own line with no "- " (2026-10-14:
// four sets, one per line). Markdown would run those lines into one
// paragraph; each becomes its own, so a chip's panel opens under its line.
// Lists, tables, indented continuations, math blocks and code are left alone.
const LIST_ITEM = /^\s*(?:[-*+]|\d+[.)])\s/;
export function lineBlocks(md) {
  const lines = md.split('\n');
  const out = [];
  let fenced = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // A fence opens or closes the block; "$$x$$" on one line does neither.
    if (/^\s*```/.test(line) || (line.match(/\$\$/g) || []).length % 2 === 1) fenced = !fenced;
    out.push(line);
    const next = lines[i + 1];
    if (fenced || next == null || !line.trim() || !next.trim()) continue;
    if (/^\s*\|/.test(line) || /^\s*\|/.test(next) || /^\s*(```|\$\$)/.test(next)) continue;
    if (LIST_ITEM.test(next) || /^\s/.test(next)) continue;
    out.push('');
  }
  return out.join('\n');
}

// [6 students](students:ID,ID,…), up to 30 ids.
const GROUP_RE = /\[([^\]\n]{1,80})\]\(students:\s*([\w.-]{1,64}(?:\s*,\s*[\w.-]{1,64}){0,29})\s*\)([.,;:!?)]*)/g;
const groupIds = list => [...new Set(String(list).split(',').map(x => x.trim()).filter(x => ID.test(x)))];

export function inlineMarkdown(text, items) {
  const bySlot = new Map(items.filter(i => i.slot != null).map(i => [i.slot, i]));
  const used = new Set();
  let out = String(text ?? '').replace(/\d*$/, '');
  out = out.replace(/(\S?)(\d+)([.,;:!?)]*)/g, (m, before, n, punct) => {
    const it = bySlot.get(Number(n));
    if (!it) return before + punct;
    used.add(it.id);
    return `${before}${before ? ' ' : ''}[${it.label}](#bb-${it.id}${enc(punct)})`;
  });
  out = out.replace(GROUP_RE, (m, label, ids, punct) => `[${cleanLabel(label)}](#bb-group-${groupIds(ids).join('+')}${enc(punct)})`);
  out = out.replace(/\[([^\]\n]{1,80})\]\((student|hw):([\w.-]{1,64})\)([.,;:!?)]*)/g, (m, label, kind, id, punct) => `[${cleanLabel(label)}](#bb-${kind}-${id}${enc(punct)})`);
  out = lineBlocks(out);
  const trailing = items.filter(i => !used.has(i.id));
  if (trailing.length) out = `${out.replace(/\s+$/, '')}\n\n${trailing.map(i => `[${i.label}](#bb-${i.id})`).join(' ')}`;
  return out;
}

// One chip, from a #bb- link. Anything else stays an ordinary link, styled as
// the house quiet link (bi-link) so it reads as one (2026-10-29: Buffy's link
// to the diagnostic guide's script looked like plain text).
export function ChipLink({ href = '', children }) {
  const ctx = useContext(ReplyContext);
  const m = /^#bb-([\w.+-]+?)(?:~(.*))?$/.exec(href);
  if (!m || !ctx) return <a className="bi-link" href={href}>{children}</a>;
  const [, id, rawPunct] = m;
  let punct = '';
  try { punct = rawPunct ? decodeURIComponent(rawPunct) : ''; } catch { punct = ''; }
  const label = textOf(children);
  let chip = null;
  if (id.startsWith('student-') && ID.test(id.slice(8))) chip = <StudentChip id={id.slice(8)} label={label} />;
  else if (id.startsWith('group-')) chip = <GroupChip ids={id.slice(6).split('+').filter(x => ID.test(x))} label={label} />;
  else if (id.startsWith('hw-') && ID.test(id.slice(3))) chip = <HomeworkChip id={id.slice(3)} label={label} />;
  else {
    const it = ctx.items.find(i => i.id === id);
    if (!it) return <>{children}{punct}</>;
    if (it.kind === 'showQuestion') chip = <QuestionChip item={{ ...it.params, label: it.label }} />;
    else if (DIALOG_ACTION_KINDS.includes(it.kind)) chip = <ChatActionChip inline action={it.action} auth={ctx.auth} classId={ctx.classId} onDone={ctx.onDone} />;
    else if (it.kind === 'unit') chip = <Chip tone="action" icon={<ArrowRight />} onClick={() => ctx.onUnit?.(it.action)}>{it.label}</Chip>;
    else return <>{children}{punct}</>;
  }
  return <span className="bi-glue">{chip}{punct}</span>;
}

// Paragraphs and list items each get a slot right after them, so a chip's
// panel opens under its own line.
const SlotP = ({ node, children, ...rest }) => <SlotBlock render={(slot, here) => <SlotContext.Provider value={slot}><p {...rest}>{children}</p>{here}</SlotContext.Provider>} />;
const SlotLi = ({ node, children, ...rest }) => <SlotBlock render={(slot, here) => <SlotContext.Provider value={slot}><li {...rest}>{children}{here}</li></SlotContext.Provider>} />;
const COMPONENTS = { a: ChipLink, p: SlotP, li: SlotLi };

// ctx: { auth, classId, students?, onDone?(note), onUnit?(action) }
export function InlineReply({ text, items = [], streaming = false, ctx }) {
  // Held before the rewrite: it appends trailing chips, so a half-arrived link
  // would no longer sit at the end.
  const md = useMemo(() => inlineMarkdown(streaming ? holdOpenLink(text) : text, items), [text, items, streaming]);
  const value = useMemo(() => ({ ...ctx, items, streaming }), [ctx, items, streaming]);
  return <ReplyContext.Provider value={value}><BuffyMarkdown streaming={streaming} components={COMPONENTS}>{md}</BuffyMarkdown></ReplyContext.Provider>;
}

// For the docked chat's light renderer (AIAssistant's RichLine): one line's
// text with its chips. Returns null when the line has nothing inline.
const TOKEN = /(\d+)([.,;:!?)]*)|\[([^\]\n]{1,80})\]\((student|hw):([\w.-]{1,64})\)([.,;:!?)]*)|\[([^\]\n]{1,80})\]\(students:\s*([\w.-]{1,64}(?:\s*,\s*[\w.-]{1,64}){0,29})\s*\)([.,;:!?)]*)/g;
export function inlineParts(line) {
  const text = String(line ?? '').replace(/\d*$/, '');
  if (!/[]|\]\((students?|hw):/.test(text)) return null;
  const parts = [];
  let last = 0;
  for (const m of text.matchAll(TOKEN)) {
    if (m.index > last) parts.push({ text: text.slice(last, m.index) });
    if (m[1] !== undefined) parts.push({ slot: Number(m[1]), punct: m[2] || '' });
    else if (m[8] !== undefined) parts.push({ ref: 'group', id: groupIds(m[8]).join('+'), label: cleanLabel(m[7]), punct: m[9] || '' });
    else parts.push({ ref: m[4], id: m[5], label: cleanLabel(m[3]), punct: m[6] || '' });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}
export function InlineChipFor({ part }) {
  const ctx = useContext(ReplyContext);
  if (!ctx) return part.label || null;
  const href = part.ref ? `#bb-${part.ref}-${part.id}` : `#bb-s${part.slot}`;
  const it = part.ref ? null : ctx.items.find(i => i.slot === part.slot);
  if (!part.ref && !it) return part.punct || null;
  return <ChipLink href={`${href}${enc(part.punct)}`}>{part.ref ? part.label : it.label}</ChipLink>;
}
// Chips with no slot (older tags, coverage picks) go at the end of the reply.
export function TrailingChips() {
  const ctx = useContext(ReplyContext);
  const rest = (ctx?.items || []).filter(i => i.slot == null);
  if (!rest.length) return null;
  return <span className="block">{rest.map(i => <span key={i.id}><ChipLink href={`#bb-${i.id}`}>{i.label}</ChipLink> </span>)}</span>;
}
export function ReplyProvider({ ctx, items, children }) {
  const value = useMemo(() => ({ ...ctx, items }), [ctx, items]);
  return <ReplyContext.Provider value={value}>{children}</ReplyContext.Provider>;
}
